import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceArea, Customized } from "recharts";
import { format } from "date-fns";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "./ui/chart";
import { STATUS_COLORS } from "@/components/filters/StatusFilter";
import { formatTestDuration } from "@/lib/utils";
import { buildTestUrl } from "@/lib/test-url-utils";
import { useNowMinuteTick } from "@/components/agent-session/subtaskTicker";
import { TestHistoryRun } from "@/hooks/useTestHistory";
import Link from "next/link";
import { useDashboardAppRouter as useRouter } from "@/hooks/useDashboardRouter";
import { BarChart3 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
interface RunHistoryChartProps {
  runs: TestHistoryRun[];
  org: string;
  runId: string;
  jobId: string;
  testId: string;
  currentRunId?: string;
  currentJobId?: string;
  timeRangeHours?: number; // Time range to preserve in URL
  branchFilter?: "all" | "main" | "current" | "main_and_current" | "latest_per_branch"; // Branch filter to preserve in URL
  currentBranch?: string; // Current branch for determining main vs current
  zoomStart?: number | null;
  zoomEnd?: number | null;
  onZoomChange?: (start: number | null, end: number | null) => void;
  isResizing?: boolean; // Whether parent container is being resized
  onNavigationStart?: (targetRunId: string, targetJobId: string) => void; // Callback when navigation starts
  onRunClick?: (run: TestHistoryRun) => void; // Callback when a scatter plot point is clicked
  externalHoveredTestId?: string | null; // External hover state from failure card
}
interface ChartDataPoint {
  timestamp: number; // Unix timestamp in milliseconds
  duration: number | null; // Duration in seconds, null when timing data unavailable (for tooltip)
  durationForPlot: number; // Duration for Y-axis plotting (0 when no data)
  status: "pass" | "fail" | "error";
  fill: string;
  run: TestHistoryRun; // Full run data for tooltip
  isOutlier?: boolean; // Duration exceeds the p99-clamped Y domain; pinned at the top edge
  isLowOutlier?: boolean; // Duration below the p1-clamped Y domain (or no timing data); pinned at the bottom edge
}
function quantile(sorted: number[], q: number): number {
  if (sorted.length === 0) {
    return 0;
  }
  const pos = (sorted.length - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.min(sorted.length - 1, lo + 1);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

// Helper function to parse timestamp as UTC
// API returns timestamps like "2025-12-18 22:45:05" in UTC
const parseUTCTimestamp = (ts: string): Date => {
  // If already has timezone indicator, parse as-is
  if (ts.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(ts)) {
    return new Date(ts);
  }
  // Replace space with 'T' for ISO format and append 'Z' for UTC
  const isoFormat = ts.replace(" ", "T") + "Z";
  return new Date(isoFormat);
};

// Legend filter groups
const STATUS_GROUP = ["passed", "failed"];
const BRANCH_GROUP = ["main", "current", "other"];

// Helper function to check if branch is main branch
const isMainBranch = (branch: string | null | undefined): boolean => {
  if (!branch) return false;
  const normalizedBranch = branch.toLowerCase().trim();
  return normalizedBranch === "main" || normalizedBranch === "master" || normalizedBranch === "trunk";
};

// Helper function to check if branch matches current branch
const isCurrentBranch = (branch: string | null | undefined, currentBranch: string | null | undefined): boolean => {
  if (!branch || !currentBranch) return false;
  return branch.toLowerCase().trim() === currentBranch.toLowerCase().trim();
};

// Custom dot renderer: soft feathered dots — small neutral for passes, larger
// glowing red for failures, a ring for the current run, and up/down arrows
// for runs pinned at the clamped top/bottom edges of the Y domain.
const CustomDot = props => {
  const $ = _c(45);
  const {
    cx,
    cy,
    payload,
    currentTestId,
    isHovered
  } = props;
  if (cx == null || cy == null) {
    return null;
  }
  const isCurrent = payload?.run?.test_id === currentTestId;
  const isFail = payload?.status !== "pass";
  const cursor = isCurrent ? "default" : "pointer";
  let t0;
  if ($[0] !== cx || $[1] !== cy || $[2] !== isCurrent) {
    t0 = isCurrent ? <circle cx={cx} cy={cy} r={7} fill="none" stroke="var(--chart-line-neutral)" strokeWidth={1.25} strokeOpacity={0.9} /> : null;
    $[0] = cx;
    $[1] = cy;
    $[2] = isCurrent;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const currentRunRing = t0;
  if (payload?.isOutlier || payload?.isLowOutlier) {
    const s = isHovered ? 5.5 : 4.5;
    const arrow = payload?.isOutlier ? `M${cx},${cy - s} L${cx + s},${cy + s * 0.8} L${cx - s},${cy + s * 0.8} Z` : `M${cx},${cy + s} L${cx + s},${cy - s * 0.8} L${cx - s},${cy - s * 0.8} Z`;
    let t1;
    if ($[4] !== cursor) {
      t1 = {
        cursor
      };
      $[4] = cursor;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    const t2 = isFail ? "var(--chart-5)" : "var(--chart-tick-label-color)";
    const t3 = isFail ? 0.9 : 0.55;
    let t4;
    if ($[6] !== arrow || $[7] !== t2 || $[8] !== t3) {
      t4 = <path d={arrow} fill={t2} opacity={t3} />;
      $[6] = arrow;
      $[7] = t2;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== currentRunRing || $[11] !== t1 || $[12] !== t4) {
      t5 = <g style={t1}>{t4}{currentRunRing}</g>;
      $[10] = currentRunRing;
      $[11] = t1;
      $[12] = t4;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  if (isFail) {
    let t1;
    if ($[14] !== cursor) {
      t1 = {
        cursor
      };
      $[14] = cursor;
      $[15] = t1;
    } else {
      t1 = $[15];
    }
    const t2 = isHovered ? 10 : 8;
    let t3;
    if ($[16] !== cx || $[17] !== cy || $[18] !== t2) {
      t3 = <circle cx={cx} cy={cy} r={t2} fill="url(#run-history-fail-glow)" />;
      $[16] = cx;
      $[17] = cy;
      $[18] = t2;
      $[19] = t3;
    } else {
      t3 = $[19];
    }
    const t4 = isHovered ? 5.5 : 4.5;
    let t5;
    if ($[20] !== cx || $[21] !== cy || $[22] !== t4) {
      t5 = <circle cx={cx} cy={cy} r={t4} fill="url(#run-history-fail-dot)" />;
      $[20] = cx;
      $[21] = cy;
      $[22] = t4;
      $[23] = t5;
    } else {
      t5 = $[23];
    }
    let t6;
    if ($[24] !== currentRunRing || $[25] !== t1 || $[26] !== t3 || $[27] !== t5) {
      t6 = <g style={t1}>{t3}{t5}{currentRunRing}</g>;
      $[24] = currentRunRing;
      $[25] = t1;
      $[26] = t3;
      $[27] = t5;
      $[28] = t6;
    } else {
      t6 = $[28];
    }
    return t6;
  }
  let t1;
  if ($[29] !== cursor) {
    t1 = {
      cursor
    };
    $[29] = cursor;
    $[30] = t1;
  } else {
    t1 = $[30];
  }
  let t2;
  if ($[31] !== cx || $[32] !== cy) {
    t2 = <circle cx={cx} cy={cy} r={8} fill="transparent" />;
    $[31] = cx;
    $[32] = cy;
    $[33] = t2;
  } else {
    t2 = $[33];
  }
  const t3 = isHovered ? 4.5 : 3.25;
  const t4 = isCurrent ? "url(#run-history-current-dot)" : "url(#run-history-pass-dot)";
  const t5 = isHovered || isCurrent ? 1 : 0.75;
  let t6;
  if ($[34] !== cx || $[35] !== cy || $[36] !== t3 || $[37] !== t4 || $[38] !== t5) {
    t6 = <circle cx={cx} cy={cy} r={t3} fill={t4} opacity={t5} />;
    $[34] = cx;
    $[35] = cy;
    $[36] = t3;
    $[37] = t4;
    $[38] = t5;
    $[39] = t6;
  } else {
    t6 = $[39];
  }
  let t7;
  if ($[40] !== currentRunRing || $[41] !== t1 || $[42] !== t2 || $[43] !== t6) {
    t7 = <g style={t1}>{t2}{t6}{currentRunRing}</g>;
    $[40] = currentRunRing;
    $[41] = t1;
    $[42] = t2;
    $[43] = t6;
    $[44] = t7;
  } else {
    t7 = $[44];
  }
  return t7;
};
function RunTooltip(t0) {
  const $ = _c(43);
  const {
    active,
    payload,
    testId,
    formatDuration,
    onHoverChange
  } = t0;
  let t1;
  let t2;
  if ($[0] !== active || $[1] !== onHoverChange || $[2] !== payload) {
    t1 = () => {
      if (!active || !payload || payload.length === 0) {
        onHoverChange(null);
        return;
      }
      const data = payload[0].payload;
      if (!data || !data.run) {
        onHoverChange(null);
        return;
      }
      const hoveredTestId = data.run.test_id;
      onHoverChange(hoveredTestId ? {
        testId: hoveredTestId
      } : null);
    };
    t2 = [active, payload, onHoverChange];
    $[0] = active;
    $[1] = onHoverChange;
    $[2] = payload;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const data_0 = payload[0].payload;
  if (!data_0 || !data_0.run) {
    return null;
  }
  const {
    run
  } = data_0;
  let t3;
  if ($[5] !== run.sha) {
    t3 = run.sha ? run.sha.substring(0, 7) : null;
    $[5] = run.sha;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const shortSha = t3;
  let t4;
  if ($[7] !== run.timestamp) {
    t4 = run.timestamp ? format(parseUTCTimestamp(run.timestamp), "MMM d, h:mm a") : null;
    $[7] = run.timestamp;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const timestamp = t4;
  const isPass = run.status === "pass";
  const statusColor = isPass ? STATUS_COLORS.success : STATUS_COLORS.failure;
  const statusLabel = isPass ? "Passed" : "Failed";
  const isCurrent = data_0.run.test_id === testId;
  let t5;
  if ($[9] !== run.commit_message) {
    t5 = run.commit_message ? (() => {
      const firstLine = run.commit_message.split("\n")[0];
      return firstLine.length > 100 ? `${firstLine.substring(0, 100)}...` : firstLine;
    })() : null;
    $[9] = run.commit_message;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const commitTitle = t5;
  let t6;
  if ($[11] !== timestamp) {
    t6 = timestamp && <div className="text-neutral-900 dark:text-white">{timestamp}</div>;
    $[11] = timestamp;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== isCurrent) {
    t7 = isCurrent && <Badge variant="muted" className="mb-2">Current run</Badge>;
    $[13] = isCurrent;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== statusColor) {
    t8 = <div className="w-[3px] h-4 rounded-sm" style={{
      backgroundColor: statusColor
    }} />;
    $[15] = statusColor;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== statusLabel) {
    t9 = <span className="font-semibold text-neutral-900 dark:text-white">{statusLabel}</span>;
    $[17] = statusLabel;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <div className="flex items-center gap-2">{t8}{t9}</div>;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== data_0.duration || $[23] !== data_0.isLowOutlier || $[24] !== data_0.isOutlier || $[25] !== formatDuration) {
    t11 = <div className="flex items-center gap-2 pt-1 border-t border-dashed border-[var(--color-border)] dark:border-[var(--color-border)]">{data_0.duration != null ? <><span className="font-semibold text-neutral-900 dark:text-white">{formatDuration(data_0.duration)}</span><span className="text-neutral-500 dark:text-neutral-400">Duration{data_0.isOutlier ? " (>p99)" : data_0.isLowOutlier ? " (<p1)" : ""}</span></> : <span className="text-neutral-500 dark:text-neutral-400">No timing data</span>}</div>;
    $[22] = data_0.duration;
    $[23] = data_0.isLowOutlier;
    $[24] = data_0.isOutlier;
    $[25] = formatDuration;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== run.branch) {
    t12 = run.branch && <div className="flex items-center gap-2 text-neutral-600 dark:text-neutral-400"><span className="truncate max-w-[200px]">{run.branch}</span></div>;
    $[27] = run.branch;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== run.pr_number || $[30] !== shortSha) {
    t13 = (shortSha || run.pr_number) && <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400">{shortSha && <span className="font-mono">{shortSha}</span>}{shortSha && run.pr_number && <span>·</span>}{run.pr_number && <span>PR #{run.pr_number}</span>}</div>;
    $[29] = run.pr_number;
    $[30] = shortSha;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== commitTitle || $[33] !== run.commit_message) {
    t14 = commitTitle && <div className="text-neutral-600 dark:text-neutral-400 truncate max-w-[280px]" title={run.commit_message?.split("\n")[0]}>{commitTitle}</div>;
    $[32] = commitTitle;
    $[33] = run.commit_message;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t12 || $[38] !== t13 || $[39] !== t14 || $[40] !== t6 || $[41] !== t7) {
    t15 = <div className="rounded-lg border border-[var(--color-border)] dark:border-[var(--color-border)] bg-white dark:bg-[#2A2C2C] shadow-lg p-3 min-w-[180px] type-body-xs-regular"><div className="space-y-2">{t6}{t7}{t10}{t11}{t12}{t13}{t14}</div></div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
    $[40] = t6;
    $[41] = t7;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  return t15;
}
type LegendCounts = Record<"passed" | "failed" | "main" | "current" | "other", number>;
function RunHistoryLegend(t0) {
  const $ = _c(7);
  const {
    branchFilter,
    currentBranch,
    hiddenSeries,
    legendCounts,
    onToggle,
    onSolo
  } = t0;
  let t1;
  if ($[0] !== branchFilter || $[1] !== currentBranch || $[2] !== hiddenSeries || $[3] !== legendCounts || $[4] !== onSolo || $[5] !== onToggle) {
    const legendItems = [{
      key: "passed",
      label: "Passed",
      color: "var(--chart-tick-label-color)",
      shape: "circle"
    }, {
      key: "failed",
      label: "Failed",
      color: "var(--chart-5)",
      shape: "circle"
    }];
    const branchLegendMode = branchFilter ?? "all";
    const showMainCurrentOther = branchLegendMode === "all";
    const showMainAndCurrent = branchLegendMode === "main_and_current";
    const showMainOnly = branchLegendMode === "main";
    const showCurrentOnly = branchLegendMode === "current";
    const currentIsMain = isMainBranch(currentBranch);
    if (showMainCurrentOther || showMainAndCurrent || showMainOnly) {
      if (!currentIsMain || !showMainCurrentOther && !showMainAndCurrent) {
        legendItems.push({
          key: "main",
          label: "Main branch",
          color: "var(--chart-9)",
          shape: "band"
        });
      }
    }
    if (showMainCurrentOther || showMainAndCurrent || showCurrentOnly) {
      legendItems.push({
        key: "current",
        label: "Current branch",
        color: "var(--chart-7)",
        shape: "band"
      });
    }
    if (showMainCurrentOther) {
      legendItems.push({
        key: "other",
        label: "Other branches",
        color: "var(--chart-tick-label-color)",
        shape: "band"
      });
    }
    const renderShape = _temp;
    const statusItems = legendItems.filter(_temp2);
    const branchItems = legendItems.filter(_temp3);
    const renderLegendItem = (item_1, index) => {
      const isHidden_0 = hiddenSeries.has(item_1.key);
      return <div key={item_1.key} onMouseDown={_temp4} className={`flex items-center gap-1.5 px-1.5 py-0.5 rounded type-body-xs-regular transition-all ${isHidden_0 ? "opacity-50 text-neutral-400 dark:text-neutral-500" : "text-neutral-500 dark:text-neutral-400"}`}><button onClick={() => onToggle(item_1.key)} className="hover:scale-110 transition-transform cursor-pointer" title="Toggle visibility">{renderShape(item_1.shape, item_1.color, isHidden_0)}</button><button onClick={() => onSolo(item_1.key)} className={`hover:underline cursor-pointer ${isHidden_0 ? "line-through" : ""}`} title="Show only this">{item_1.label} ({legendCounts[item_1.key as keyof LegendCounts]})</button></div>;
    };
    t1 = <div className="flex items-center justify-end gap-1.5 mb-1"><div className="flex items-center gap-3">{statusItems.map(renderLegendItem)}</div>{statusItems.length > 0 && branchItems.length > 0 && <div className="w-[5px]" />}{branchItems.length > 1 ? <div className="flex items-center gap-3 px-2 py-1 border rounded border-[#E0E0E0] dark:border-[#414244]">{branchItems.map(renderLegendItem)}</div> : <div className="flex items-center gap-3">{branchItems.map(renderLegendItem)}</div>}</div>;
    $[0] = branchFilter;
    $[1] = currentBranch;
    $[2] = hiddenSeries;
    $[3] = legendCounts;
    $[4] = onSolo;
    $[5] = onToggle;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}
function _temp4(e) {
  return e.stopPropagation();
}
function _temp3(item_0) {
  return BRANCH_GROUP.includes(item_0.key);
}
function _temp2(item) {
  return STATUS_GROUP.includes(item.key);
}
function _temp(shape, color, isHidden) {
  const opacity = isHidden ? 0.3 : 1;
  if (shape === "band") {
    return <svg width="12" height="12" viewBox="0 0 12 12" style={{
      opacity
    }}><rect x="0" y="3.5" width="12" height="5" rx="1.5" fill={color} opacity="0.35" /><line x1="4" y1="4" x2="4" y2="8" stroke={color} strokeWidth="1.25" /><line x1="8" y1="4" x2="8" y2="8" stroke={color} strokeWidth="1.25" /></svg>;
  }
  return <svg width="12" height="12" viewBox="0 0 12 12" style={{
    opacity
  }}><circle cx="6" cy="6" r="5" fill={color} opacity="0.8" /></svg>;
}
export const RunHistoryChart = React.memo(function RunHistoryChart(t0) {
  const $ = _c(191);
  const {
    runs,
    org,
    runId,
    jobId,
    testId,
    timeRangeHours,
    branchFilter,
    currentBranch,
    zoomStart,
    zoomEnd,
    onZoomChange,
    isResizing: t1,
    onNavigationStart,
    onRunClick,
    externalHoveredTestId: t2
  } = t0;
  const isResizing = t1 === undefined ? false : t1;
  const externalHoveredTestId = t2 === undefined ? null : t2;
  const router = useRouter();
  const [refAreaLeft, setRefAreaLeft] = useState(null);
  const [refAreaRight, setRefAreaRight] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartXRef = useRef(null);
  const plotStartXRef = useRef(null);
  const chartContainerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(800);
  const [hoveredDot, setHoveredDot] = useState(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Set();
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [hiddenSeries, setHiddenSeries] = useState(t3);
  const getGroup = _temp5;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = key_0 => {
      setHiddenSeries(prev => {
        const next = new Set(prev);
        if (next.has(key_0)) {
          next.delete(key_0);
        } else {
          next.add(key_0);
        }
        return next;
      });
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const toggleLegendItem = t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = key_1 => {
      const group = getGroup(key_1);
      setHiddenSeries(prev_0 => {
        const next_0 = new Set(prev_0);
        const visibleInGroup = group.filter(k => !prev_0.has(k));
        if (visibleInGroup.length === 1 && visibleInGroup[0] === key_1) {
          group.forEach(k_0 => next_0.delete(k_0));
        } else {
          group.forEach(k_1 => {
            if (k_1 === key_1) {
              next_0.delete(k_1);
            } else {
              next_0.add(k_1);
            }
          });
        }
        return next_0;
      });
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const soloLegendItem = t5;
  let durations;
  let fail;
  let pass;
  let timestamps;
  if ($[3] !== externalHoveredTestId || $[4] !== jobId || $[5] !== org || $[6] !== runId || $[7] !== runs || $[8] !== testId) {
    pass = [];
    fail = [];
    timestamps = [];
    durations = [];
    runs.filter(_temp6).forEach(run_0 => {
      const timestamp = parseUTCTimestamp(run_0.timestamp).getTime();
      timestamps.push(timestamp);
      const duration = run_0.duration_seconds;
      if (duration != null) {
        durations.push(duration);
      }
      const point = {
        timestamp,
        duration,
        durationForPlot: duration ?? 0,
        status: run_0.status as "pass" | "fail" | "error",
        fill: run_0.status === "pass" ? STATUS_COLORS.success : STATUS_COLORS.failure,
        run: run_0,
        org,
        runId,
        jobId,
        testId
      } as any;
      if (run_0.status === "pass") {
        pass.push(point);
      } else {
        fail.push(point);
      }
    });
    let t6;
    if ($[13] !== testId) {
      t6 = point_0 => point_0.run?.test_id === testId;
      $[13] = testId;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    const isCurrentRun = t6;
    let t7;
    if ($[15] !== externalHoveredTestId) {
      t7 = point_1 => externalHoveredTestId && point_1.run?.test_id === externalHoveredTestId;
      $[15] = externalHoveredTestId;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    const isHoveredRun = t7;
    let t8;
    if ($[17] !== isCurrentRun || $[18] !== isHoveredRun) {
      t8 = (a, b) => {
        const aIsCurrent = isCurrentRun(a);
        const bIsCurrent = isCurrentRun(b);
        const aIsHovered = isHoveredRun(a);
        const bIsHovered = isHoveredRun(b);
        if (aIsHovered && !bIsHovered) {
          return 1;
        }
        if (!aIsHovered && bIsHovered) {
          return -1;
        }
        if (aIsCurrent && !bIsCurrent) {
          return 1;
        }
        if (!aIsCurrent && bIsCurrent) {
          return -1;
        }
        return 0;
      };
      $[17] = isCurrentRun;
      $[18] = isHoveredRun;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    pass.sort(t8);
    let t9;
    if ($[20] !== isCurrentRun || $[21] !== isHoveredRun) {
      t9 = (a_0, b_0) => {
        const aIsCurrent_0 = isCurrentRun(a_0);
        const bIsCurrent_0 = isCurrentRun(b_0);
        const aIsHovered_0 = isHoveredRun(a_0);
        const bIsHovered_0 = isHoveredRun(b_0);
        if (aIsHovered_0 && !bIsHovered_0) {
          return 1;
        }
        if (!aIsHovered_0 && bIsHovered_0) {
          return -1;
        }
        if (aIsCurrent_0 && !bIsCurrent_0) {
          return 1;
        }
        if (!aIsCurrent_0 && bIsCurrent_0) {
          return -1;
        }
        return 0;
      };
      $[20] = isCurrentRun;
      $[21] = isHoveredRun;
      $[22] = t9;
    } else {
      t9 = $[22];
    }
    fail.sort(t9);
    $[3] = externalHoveredTestId;
    $[4] = jobId;
    $[5] = org;
    $[6] = runId;
    $[7] = runs;
    $[8] = testId;
    $[9] = durations;
    $[10] = fail;
    $[11] = pass;
    $[12] = timestamps;
  } else {
    durations = $[9];
    fail = $[10];
    pass = $[11];
    timestamps = $[12];
  }
  let dateRangeDays = 0;
  if (timestamps.length > 0) {
    const minTimestamp = Math.min(...timestamps);
    const maxTimestamp = Math.max(...timestamps);
    const diffMs = maxTimestamp - minTimestamp;
    dateRangeDays = diffMs / 86400000;
  }
  const maxDuration = durations.length > 0 ? Math.max(...durations) : 0;
  let t6;
  if ($[23] !== dateRangeDays || $[24] !== fail || $[25] !== maxDuration || $[26] !== pass) {
    t6 = {
      passData: pass,
      failData: fail,
      dateRangeDays,
      maxDuration
    };
    $[23] = dateRangeDays;
    $[24] = fail;
    $[25] = maxDuration;
    $[26] = pass;
    $[27] = t6;
  } else {
    t6 = $[27];
  }
  const {
    passData,
    failData,
    maxDuration: maxDuration_0
  } = t6;
  let t7;
  if ($[28] !== timeRangeHours) {
    t7 = tickItem => {
      if (!tickItem || isNaN(tickItem)) {
        return "";
      }
      const date = new Date(tickItem);
      if (isNaN(date.getTime())) {
        return "";
      }
      const withinThreeDays = (timeRangeHours || 24) <= 72;
      return format(date, withinThreeDays ? "MMM d HH:mm" : "MMM d");
    };
    $[28] = timeRangeHours;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const formatXAxisTick = t7;
  let t8;
  if ($[30] !== maxDuration_0) {
    t8 = tickItem_0 => {
      if (tickItem_0 == null || isNaN(tickItem_0)) {
        return "";
      }
      if (maxDuration_0 < 0.002) {
        const ns = Math.round(tickItem_0 * 1000000000);
        return `${ns.toLocaleString()}ns`;
      }
      if (maxDuration_0 < 3) {
        const ms = Math.round(tickItem_0 * 1000);
        return `${ms.toLocaleString()}ms`;
      }
      return `${Math.round(tickItem_0).toLocaleString()}s`;
    };
    $[30] = maxDuration_0;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  const formatYAxisTick = t8;
  let t9;
  if ($[32] !== maxDuration_0) {
    t9 = seconds => {
      if (seconds == null || isNaN(seconds)) {
        return "\u2014";
      }
      if (maxDuration_0 < 0.002) {
        const ns_0 = Math.round(seconds * 1000000000);
        return `${ns_0.toLocaleString()}ns`;
      }
      return formatTestDuration(seconds);
    };
    $[32] = maxDuration_0;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const formatTooltipDuration = t9;
  let t10;
  if ($[34] !== branchFilter || $[35] !== currentBranch || $[36] !== hiddenSeries) {
    t10 = d => {
      if (d.status === "pass" && hiddenSeries.has("passed")) {
        return true;
      }
      if ((d.status === "fail" || d.status === "error") && hiddenSeries.has("failed")) {
        return true;
      }
      if (branchFilter === "latest_per_branch") {
        return false;
      }
      const runBranch = d.run?.branch;
      const currentIsMain = isMainBranch(currentBranch);
      const legendIsConsolidated = currentIsMain && (branchFilter === "all" || branchFilter === "main_and_current");
      if (hiddenSeries.has("main") && isMainBranch(runBranch) && !legendIsConsolidated) {
        return true;
      }
      if (hiddenSeries.has("current")) {
        if (legendIsConsolidated) {
          if (isMainBranch(runBranch)) {
            return true;
          }
        } else {
          if (isCurrentBranch(runBranch, currentBranch) && !isMainBranch(runBranch)) {
            return true;
          }
        }
      }
      if (hiddenSeries.has("other") && !isMainBranch(runBranch) && !isCurrentBranch(runBranch, currentBranch)) {
        return true;
      }
      return false;
    };
    $[34] = branchFilter;
    $[35] = currentBranch;
    $[36] = hiddenSeries;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const shouldHidePoint = t10;
  const minuteNow = useNowMinuteTick(zoomStart == null || zoomEnd == null);
  const now = minuteNow + 60000;
  const hoursInMs = (timeRangeHours || 24) * 60 * 60 * 1000;
  const defaultStart = now - hoursInMs;
  const t11 = zoomStart ?? defaultStart;
  const t12 = zoomEnd ?? now;
  let t13;
  if ($[38] !== t11 || $[39] !== t12) {
    t13 = [t11, t12];
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const currentXDomain = t13 as [number, number];
  let t14;
  if ($[41] !== currentXDomain || $[42] !== failData || $[43] !== maxDuration_0 || $[44] !== passData) {
    bb0: {
      const [domainStart, domainEnd] = currentXDomain;
      const durations_0 = [];
      for (const d_0 of [...passData, ...failData]) {
        if (d_0.timestamp >= domainStart && d_0.timestamp <= domainEnd && d_0.duration != null) {
          durations_0.push(d_0.durationForPlot);
        }
      }
      durations_0.sort(_temp7);
      if (durations_0.length === 0) {
        const t15 = maxDuration_0 > 0 ? maxDuration_0 : 1;
        let t16;
        if ($[46] !== t15) {
          t16 = {
            yMin: 0,
            yMax: t15
          };
          $[46] = t15;
          $[47] = t16;
        } else {
          t16 = $[47];
        }
        t14 = t16;
        break bb0;
      }
      const p1 = quantile(durations_0, 0.01);
      const p99 = quantile(durations_0, 0.99);
      const pad = Math.max((p99 - p1) * 0.08, p99 * 0.02);
      const t15 = Math.max(0, p1 - pad);
      const t16 = p99 + pad;
      let t17;
      if ($[48] !== t15 || $[49] !== t16) {
        t17 = {
          yMin: t15,
          yMax: t16
        };
        $[48] = t15;
        $[49] = t16;
        $[50] = t17;
      } else {
        t17 = $[50];
      }
      t14 = t17;
    }
    $[41] = currentXDomain;
    $[42] = failData;
    $[43] = maxDuration_0;
    $[44] = passData;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  const yDomain = t14;
  let t15;
  if ($[51] !== yDomain) {
    t15 = d_1 => {
      if (d_1.durationForPlot > yDomain.yMax) {
        return {
          ...d_1,
          durationForPlot: yDomain.yMax,
          isOutlier: true
        };
      }
      if (d_1.durationForPlot < yDomain.yMin) {
        return {
          ...d_1,
          durationForPlot: yDomain.yMin,
          isLowOutlier: true
        };
      }
      return d_1;
    };
    $[51] = yDomain;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  const clampToYDomain = t15;
  const [domainStart_0, domainEnd_0] = currentXDomain;
  let counts;
  if ($[53] !== branchFilter || $[54] !== currentBranch || $[55] !== domainEnd_0 || $[56] !== domainStart_0 || $[57] !== failData || $[58] !== passData) {
    counts = {
      passed: 0,
      failed: 0,
      main: 0,
      current: 0,
      other: 0
    };
    const currentIsMain_0 = isMainBranch(currentBranch);
    const legendIsConsolidated_0 = currentIsMain_0 && (branchFilter === "all" || branchFilter === "main_and_current");
    for (const d_2 of [...passData, ...failData]) {
      if (d_2.timestamp < domainStart_0 || d_2.timestamp > domainEnd_0) {
        continue;
      }
      if (d_2.status === "pass") {
        counts.passed = counts.passed + 1;
      } else {
        counts.failed = counts.failed + 1;
      }
      const runBranch_0 = d_2.run?.branch;
      const onMain = isMainBranch(runBranch_0);
      const onCurrent = isCurrentBranch(runBranch_0, currentBranch);
      if (onMain) {
        counts.main = counts.main + 1;
      }
      if (legendIsConsolidated_0 ? onMain : onCurrent) {
        counts.current = counts.current + 1;
      }
      if (!onMain && !onCurrent) {
        counts.other = counts.other + 1;
      }
    }
    $[53] = branchFilter;
    $[54] = currentBranch;
    $[55] = domainEnd_0;
    $[56] = domainStart_0;
    $[57] = failData;
    $[58] = passData;
    $[59] = counts;
  } else {
    counts = $[59];
  }
  const legendCounts = counts;
  const [domainStart_1, domainEnd_1] = currentXDomain;
  let t16;
  if ($[60] !== clampToYDomain || $[61] !== domainEnd_1 || $[62] !== domainStart_1 || $[63] !== passData || $[64] !== shouldHidePoint) {
    let t17;
    if ($[66] !== domainEnd_1 || $[67] !== domainStart_1 || $[68] !== shouldHidePoint) {
      t17 = d_3 => d_3.timestamp >= domainStart_1 && d_3.timestamp <= domainEnd_1 && !shouldHidePoint(d_3);
      $[66] = domainEnd_1;
      $[67] = domainStart_1;
      $[68] = shouldHidePoint;
      $[69] = t17;
    } else {
      t17 = $[69];
    }
    t16 = passData.filter(t17).map(clampToYDomain);
    $[60] = clampToYDomain;
    $[61] = domainEnd_1;
    $[62] = domainStart_1;
    $[63] = passData;
    $[64] = shouldHidePoint;
    $[65] = t16;
  } else {
    t16 = $[65];
  }
  const filteredPassData = t16;
  const [domainStart_2, domainEnd_2] = currentXDomain;
  let t17;
  if ($[70] !== clampToYDomain || $[71] !== domainEnd_2 || $[72] !== domainStart_2 || $[73] !== failData || $[74] !== shouldHidePoint) {
    let t18;
    if ($[76] !== domainEnd_2 || $[77] !== domainStart_2 || $[78] !== shouldHidePoint) {
      t18 = d_4 => d_4.timestamp >= domainStart_2 && d_4.timestamp <= domainEnd_2 && !shouldHidePoint(d_4);
      $[76] = domainEnd_2;
      $[77] = domainStart_2;
      $[78] = shouldHidePoint;
      $[79] = t18;
    } else {
      t18 = $[79];
    }
    t17 = failData.filter(t18).map(clampToYDomain);
    $[70] = clampToYDomain;
    $[71] = domainEnd_2;
    $[72] = domainStart_2;
    $[73] = failData;
    $[74] = shouldHidePoint;
    $[75] = t17;
  } else {
    t17 = $[75];
  }
  const filteredFailData = t17;
  const [domainStart_3, domainEnd_3] = currentXDomain;
  const hoursInRange = timeRangeHours || 24;
  let intervalMs;
  if (hoursInRange <= 3) {
    intervalMs = 900000;
  } else {
    if (hoursInRange <= 12) {
      intervalMs = 3600000;
    } else {
      if (hoursInRange <= 24) {
        intervalMs = 7200000;
      } else {
        if (hoursInRange <= 48) {
          intervalMs = 14400000;
        } else {
          if (hoursInRange <= 120) {
            intervalMs = 86400000;
          } else {
            if (hoursInRange <= 336) {
              intervalMs = 172800000;
            } else {
              if (hoursInRange <= 720) {
                intervalMs = 432000000;
              } else {
                intervalMs = 1209600000;
              }
            }
          }
        }
      }
    }
  }
  const firstTick = Math.floor(domainStart_3 / intervalMs) * intervalMs;
  let ticks;
  if ($[80] !== domainEnd_3 || $[81] !== domainStart_3 || $[82] !== firstTick || $[83] !== intervalMs) {
    ticks = [];
    for (let tick = firstTick; tick <= domainEnd_3; tick = tick + intervalMs, tick) {
      if (tick >= domainStart_3) {
        ticks.push(tick);
      }
    }
    $[80] = domainEnd_3;
    $[81] = domainStart_3;
    $[82] = firstTick;
    $[83] = intervalMs;
    $[84] = ticks;
  } else {
    ticks = $[84];
  }
  const xAxisTicks = ticks;
  let t18;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      if (!chartContainerRef.current) {
        return null;
      }
      const cartesianGrid = chartContainerRef.current.querySelector(".recharts-cartesian-grid") as SVGGElement | null;
      if (!cartesianGrid) {
        return null;
      }
      const plotRect = cartesianGrid.getBoundingClientRect();
      return plotRect.left;
    };
    $[85] = t18;
  } else {
    t18 = $[85];
  }
  const getPlotStartX = t18;
  let t19;
  if ($[86] !== currentXDomain) {
    t19 = (pixelX, pixelY, plotStartX) => {
      if (!chartContainerRef.current || plotStartX == null) {
        return null;
      }
      const cartesianGrid_0 = chartContainerRef.current.querySelector(".recharts-cartesian-grid") as SVGGElement | null;
      if (!cartesianGrid_0) {
        return null;
      }
      const plotRect_0 = cartesianGrid_0.getBoundingClientRect();
      if (pixelY !== undefined) {
        if (pixelY < plotRect_0.top || pixelY > plotRect_0.bottom) {
          return null;
        }
      }
      const [xDomainStart, xDomainEnd] = currentXDomain;
      const relativeX = pixelX - plotStartX;
      const normalizedX = Math.max(0, Math.min(1, relativeX / plotRect_0.width));
      const range = xDomainEnd - xDomainStart;
      return xDomainStart + normalizedX * range;
    };
    $[86] = currentXDomain;
    $[87] = t19;
  } else {
    t19 = $[87];
  }
  const pixelToTimestamp = t19;
  let t20;
  if ($[88] !== hoveredDot || $[89] !== pixelToTimestamp) {
    t20 = e => {
      if (hoveredDot) {
        return;
      }
      const startX = getPlotStartX();
      const timestamp_0 = pixelToTimestamp(e.clientX, e.clientY, startX);
      if (timestamp_0 !== null) {
        dragStartXRef.current = e.clientX;
        setRefAreaLeft(timestamp_0);
        plotStartXRef.current = startX;
        setIsDragging(true);
      }
    };
    $[88] = hoveredDot;
    $[89] = pixelToTimestamp;
    $[90] = t20;
  } else {
    t20 = $[90];
  }
  const handleMouseDown = t20;
  let t21;
  if ($[91] !== isDragging || $[92] !== pixelToTimestamp) {
    t21 = e_0 => {
      if (!isDragging || !chartContainerRef.current || plotStartXRef.current == null) {
        return;
      }
      const timestamp_1 = pixelToTimestamp(e_0.clientX, undefined, plotStartXRef.current);
      if (timestamp_1 !== null) {
        setRefAreaRight(timestamp_1);
      }
    };
    $[91] = isDragging;
    $[92] = pixelToTimestamp;
    $[93] = t21;
  } else {
    t21 = $[93];
  }
  const handleMouseMove = t21;
  let t22;
  if ($[94] !== onZoomChange || $[95] !== refAreaLeft || $[96] !== refAreaRight) {
    t22 = e_1 => {
      if (refAreaLeft && refAreaRight && dragStartXRef.current !== null) {
        const dragDistance = Math.abs(e_1.clientX - dragStartXRef.current);
        if (dragDistance >= 50) {
          const [start, end] = [refAreaLeft, refAreaRight].sort(_temp8);
          onZoomChange?.(start, end);
        }
      }
      dragStartXRef.current = null;
      plotStartXRef.current = null;
      setRefAreaLeft(null);
      setRefAreaRight(null);
      setIsDragging(false);
    };
    $[94] = onZoomChange;
    $[95] = refAreaLeft;
    $[96] = refAreaRight;
    $[97] = t22;
  } else {
    t22 = $[97];
  }
  const handleMouseUp = t22;
  let t23;
  if ($[98] !== branchFilter || $[99] !== jobId || $[100] !== onNavigationStart || $[101] !== onRunClick || $[102] !== org || $[103] !== router || $[104] !== runId || $[105] !== testId || $[106] !== timeRangeHours) {
    t23 = (data, e_2) => {
      if (!data || !data.run) {
        return;
      }
      const run_1 = data.run as TestHistoryRun;
      if (run_1.test_id === testId) {
        return;
      }
      if (onRunClick) {
        onRunClick(run_1);
        return;
      }
      const targetRunId = run_1.run_id || runId;
      const targetJobId = run_1.job_id || jobId;
      onNavigationStart?.(targetRunId, targetJobId);
      const url = buildTestUrl({
        org,
        runId: targetRunId,
        testId: run_1.test_id,
        jobId,
        targetJobId,
        timeRangeHours,
        branchFilter
      });
      if (e_2?.nativeEvent?.metaKey || e_2?.nativeEvent?.ctrlKey) {
        window.open(url, "_blank");
      } else {
        router.push(url);
      }
    };
    $[98] = branchFilter;
    $[99] = jobId;
    $[100] = onNavigationStart;
    $[101] = onRunClick;
    $[102] = org;
    $[103] = router;
    $[104] = runId;
    $[105] = testId;
    $[106] = timeRangeHours;
    $[107] = t23;
  } else {
    t23 = $[107];
  }
  const handleScatterClick = t23;
  let t24;
  let t25;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => {
      if (!chartContainerRef.current) {
        return;
      }
      const updateWidth = () => {
        if (chartContainerRef.current) {
          setChartWidth(chartContainerRef.current.offsetWidth);
        }
      };
      updateWidth();
      const resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(chartContainerRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    };
    t25 = [];
    $[108] = t24;
    $[109] = t25;
  } else {
    t24 = $[108];
    t25 = $[109];
  }
  useEffect(t24, t25);
  let t26;
  let t27;
  if ($[110] !== handleMouseMove || $[111] !== handleMouseUp || $[112] !== isDragging) {
    t26 = () => {
      if (isDragging) {
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";
        return () => {
          window.removeEventListener("mousemove", handleMouseMove);
          window.removeEventListener("mouseup", handleMouseUp);
          document.body.style.userSelect = "";
          document.body.style.cursor = "";
        };
      }
    };
    t27 = [isDragging, handleMouseMove, handleMouseUp];
    $[110] = handleMouseMove;
    $[111] = handleMouseUp;
    $[112] = isDragging;
    $[113] = t26;
    $[114] = t27;
  } else {
    t26 = $[113];
    t27 = $[114];
  }
  useEffect(t26, t27);
  let t28;
  if ($[115] !== branchFilter || $[116] !== currentBranch || $[117] !== currentXDomain || $[118] !== filteredFailData || $[119] !== filteredPassData) {
    t28 = props => {
      const offset = props?.offset;
      const mode = branchFilter ?? "all";
      if (!offset || mode === "latest_per_branch") {
        return null;
      }
      const [d0, d1] = currentXDomain;
      const xFor = t => offset.left + (t - d0) / Math.max(1, d1 - d0) * offset.width;
      const currentIsMain_1 = isMainBranch(currentBranch);
      const consolidated = currentIsMain_1 && (mode === "all" || mode === "main_and_current");
      const bands = [];
      if ((mode === "all" || mode === "main_and_current" || mode === "main") && !consolidated) {
        bands.push({
          key: "main",
          label: "main",
          color: "var(--chart-9)",
          match: isMainBranch
        });
      }
      if (currentBranch && (mode === "all" || mode === "main_and_current" || mode === "current" || consolidated)) {
        bands.push({
          key: "current",
          label: currentBranch,
          color: "var(--chart-7)",
          match: currentIsMain_1 ? isMainBranch : branch => isCurrentBranch(branch, currentBranch)
        });
      }
      if (bands.length === 0) {
        return null;
      }
      const allPoints = [...filteredPassData, ...filteredFailData];
      return <g>{bands.map((band, i) => {
          const y0 = offset.top + offset.height + 8 + i * 12;
          const label_0 = band.label.length > 16 ? `${band.label.slice(0, 15)}…` : band.label;
          return <g key={band.key}><rect x={offset.left} y={y0} width={offset.width} height={9} rx={2} fill={band.color} opacity={0.08} />{allPoints.filter(p => band.match(p.run?.branch)).map(p_0 => <line key={p_0.run.test_id + String(p_0.timestamp)} x1={xFor(p_0.timestamp)} x2={xFor(p_0.timestamp)} y1={y0 + 1.5} y2={y0 + 9 - 1.5} stroke={p_0.status === "pass" ? band.color : "var(--chart-5)"} strokeWidth={p_0.status === "pass" ? 1.25 : 2} opacity={p_0.status === "pass" ? 0.55 : 0.95} />)}<text x={offset.left - 8} y={y0 + 9 - 1.5} textAnchor="end" fill="var(--chart-tick-label-color)" fontSize={9}>{label_0}</text></g>;
        })}</g>;
    };
    $[115] = branchFilter;
    $[116] = currentBranch;
    $[117] = currentXDomain;
    $[118] = filteredFailData;
    $[119] = filteredPassData;
    $[120] = t28;
  } else {
    t28 = $[120];
  }
  const renderBranchRugBands = t28;
  const isEmpty = zoomStart && zoomEnd ? filteredPassData.length === 0 && filteredFailData.length === 0 : passData.length === 0 && failData.length === 0;
  if (isEmpty) {
    let t29;
    if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = <div className="rounded-full bg-neutral-100 dark:bg-neutral-800 p-4"><BarChart3 className="w-8 h-8 text-neutral-400 dark:text-neutral-500" /></div>;
      $[121] = t29;
    } else {
      t29 = $[121];
    }
    let t30;
    if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = <div className="h-[363px] w-full flex items-center justify-center"><div className="flex flex-col items-center justify-center gap-5 text-center w-full h-full px-12 py-[49px] rounded-lg border border-[var(--color-border)] dark:border-[var(--color-border)] bg-neutral-50 dark:bg-neutral-900/50">{t29}<div className="space-y-2"><p className="text-base font-medium text-neutral-900 dark:text-neutral-100">No test runs to display</p><p className="text-sm text-neutral-500 dark:text-neutral-400">Adjust your time range or filters to see test runs.</p></div></div></div>;
      $[122] = t30;
    } else {
      t30 = $[122];
    }
    return t30;
  }
  const t29 = isDragging ? "col-resize" : "default";
  const t30 = isResizing ? "none" : "auto";
  let t31;
  if ($[123] !== t29 || $[124] !== t30) {
    t31 = {
      cursor: t29,
      pointerEvents: t30
    };
    $[123] = t29;
    $[124] = t30;
    $[125] = t31;
  } else {
    t31 = $[125];
  }
  let t32;
  if ($[126] !== branchFilter || $[127] !== currentBranch || $[128] !== hiddenSeries || $[129] !== legendCounts) {
    t32 = <RunHistoryLegend branchFilter={branchFilter} currentBranch={currentBranch} hiddenSeries={hiddenSeries} legendCounts={legendCounts} onToggle={toggleLegendItem} onSolo={soloLegendItem} />;
    $[126] = branchFilter;
    $[127] = currentBranch;
    $[128] = hiddenSeries;
    $[129] = legendCounts;
    $[130] = t32;
  } else {
    t32 = $[130];
  }
  let t33;
  if ($[131] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {};
    $[131] = t33;
  } else {
    t33 = $[131];
  }
  const t34 = branchFilter === "latest_per_branch" ? 30 : 58;
  const t35 = maxDuration_0 > 0 ? 20 : 10;
  let t36;
  if ($[132] !== t34 || $[133] !== t35) {
    t36 = {
      top: 10,
      right: 20,
      bottom: t34,
      left: t35
    };
    $[132] = t34;
    $[133] = t35;
    $[134] = t36;
  } else {
    t36 = $[134];
  }
  let t37;
  if ($[135] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <radialGradient id="run-history-pass-dot"><stop offset="0%" stopColor="var(--chart-tick-label-color)" stopOpacity={0.55} /><stop offset="75%" stopColor="var(--chart-tick-label-color)" stopOpacity={0.5} /><stop offset="100%" stopColor="var(--chart-tick-label-color)" stopOpacity={0} /></radialGradient>;
    $[135] = t37;
  } else {
    t37 = $[135];
  }
  let t38;
  if ($[136] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <radialGradient id="run-history-fail-dot"><stop offset="0%" stopColor="var(--chart-5)" stopOpacity={0.95} /><stop offset="78%" stopColor="var(--chart-5)" stopOpacity={0.9} /><stop offset="100%" stopColor="var(--chart-5)" stopOpacity={0} /></radialGradient>;
    $[136] = t38;
  } else {
    t38 = $[136];
  }
  let t39;
  if ($[137] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <radialGradient id="run-history-fail-glow"><stop offset="0%" stopColor="var(--chart-5)" stopOpacity={0.16} /><stop offset="100%" stopColor="var(--chart-5)" stopOpacity={0} /></radialGradient>;
    $[137] = t39;
  } else {
    t39 = $[137];
  }
  let t40;
  let t41;
  if ($[138] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <defs>{t37}{t38}{t39}<radialGradient id="run-history-current-dot"><stop offset="0%" stopColor="var(--chart-9)" stopOpacity={0.95} /><stop offset="78%" stopColor="var(--chart-9)" stopOpacity={0.9} /><stop offset="100%" stopColor="var(--chart-9)" stopOpacity={0} /></radialGradient></defs>;
    t41 = <CartesianGrid stroke="var(--chart-dotted-grid-line)" horizontal={true} vertical={true} syncWithTicks={true} opacity={0.15} strokeDasharray="2 1" />;
    $[138] = t40;
    $[139] = t41;
  } else {
    t40 = $[138];
    t41 = $[139];
  }
  const t42 = branchFilter === "latest_per_branch" ? 10 : 36;
  let t43;
  if ($[140] !== t42) {
    t43 = {
      fontSize: 12,
      className: "fill-[var(--chart-tick-label-color)]",
      textAnchor: "middle",
      dy: t42
    };
    $[140] = t42;
    $[141] = t43;
  } else {
    t43 = $[141];
  }
  let t44;
  if ($[142] !== currentXDomain || $[143] !== formatXAxisTick || $[144] !== t43 || $[145] !== xAxisTicks) {
    t44 = <XAxis type="number" dataKey="timestamp" domain={currentXDomain} ticks={xAxisTicks} tickFormatter={formatXAxisTick} tick={t43} axisLine={false} tickLine={false} allowDecimals={false} />;
    $[142] = currentXDomain;
    $[143] = formatXAxisTick;
    $[144] = t43;
    $[145] = xAxisTicks;
    $[146] = t44;
  } else {
    t44 = $[146];
  }
  let t45;
  if ($[147] !== formatYAxisTick || $[148] !== maxDuration_0 || $[149] !== yDomain) {
    t45 = maxDuration_0 > 0 && <YAxis type="number" dataKey="durationForPlot" domain={[yDomain.yMin, yDomain.yMax]} tickFormatter={formatYAxisTick} tick={{
      fontSize: 12,
      className: "fill-[var(--chart-tick-label-color)]"
    }} axisLine={false} tickLine={false} width={60} />;
    $[147] = formatYAxisTick;
    $[148] = maxDuration_0;
    $[149] = yDomain;
    $[150] = t45;
  } else {
    t45 = $[150];
  }
  let t46;
  if ($[151] !== formatTooltipDuration || $[152] !== testId) {
    t46 = <ChartTooltip content={<RunTooltip testId={testId} formatDuration={formatTooltipDuration} onHoverChange={setHoveredDot} />} cursor={false} animationDuration={0} />;
    $[151] = formatTooltipDuration;
    $[152] = testId;
    $[153] = t46;
  } else {
    t46 = $[153];
  }
  let t47;
  if ($[154] !== renderBranchRugBands) {
    t47 = <Customized component={renderBranchRugBands} />;
    $[154] = renderBranchRugBands;
    $[155] = t47;
  } else {
    t47 = $[155];
  }
  let t48;
  if ($[156] !== isDragging || $[157] !== refAreaLeft || $[158] !== refAreaRight) {
    t48 = isDragging && refAreaLeft && refAreaRight && <ReferenceArea x1={refAreaLeft} x2={refAreaRight} strokeOpacity={0.3} fill={STATUS_COLORS.success} fillOpacity={0.2} />;
    $[156] = isDragging;
    $[157] = refAreaLeft;
    $[158] = refAreaRight;
    $[159] = t48;
  } else {
    t48 = $[159];
  }
  let t49;
  if ($[160] !== branchFilter || $[161] !== currentBranch || $[162] !== externalHoveredTestId || $[163] !== filteredPassData || $[164] !== handleScatterClick || $[165] !== hoveredDot || $[166] !== testId) {
    t49 = filteredPassData.length > 0 && <Scatter name="passed" data={filteredPassData} dataKey="durationForPlot" fill={STATUS_COLORS.success} isAnimationActive={false} shape={props_0 => {
      const runTestId = props_0.payload?.run?.test_id;
      const isHovered = hoveredDot && runTestId === hoveredDot.testId || externalHoveredTestId && runTestId === externalHoveredTestId;
      return <CustomDot {...props_0} currentTestId={testId} branchFilter={branchFilter} currentBranch={currentBranch} isHovered={isHovered} />;
    }} onClick={(data_0, _index, e_3) => handleScatterClick(data_0, e_3)} />;
    $[160] = branchFilter;
    $[161] = currentBranch;
    $[162] = externalHoveredTestId;
    $[163] = filteredPassData;
    $[164] = handleScatterClick;
    $[165] = hoveredDot;
    $[166] = testId;
    $[167] = t49;
  } else {
    t49 = $[167];
  }
  let t50;
  if ($[168] !== branchFilter || $[169] !== currentBranch || $[170] !== externalHoveredTestId || $[171] !== filteredFailData || $[172] !== handleScatterClick || $[173] !== hoveredDot || $[174] !== testId) {
    t50 = filteredFailData.length > 0 && <Scatter name="failed" data={filteredFailData} dataKey="durationForPlot" fill={STATUS_COLORS.failure} isAnimationActive={false} shape={props_1 => {
      const runTestId_0 = props_1.payload?.run?.test_id;
      const isHovered_0 = hoveredDot && runTestId_0 === hoveredDot.testId || externalHoveredTestId && runTestId_0 === externalHoveredTestId;
      return <CustomDot {...props_1} currentTestId={testId} branchFilter={branchFilter} currentBranch={currentBranch} isHovered={isHovered_0} />;
    }} onClick={(data_1, _index_0, e_4) => handleScatterClick(data_1, e_4)} />;
    $[168] = branchFilter;
    $[169] = currentBranch;
    $[170] = externalHoveredTestId;
    $[171] = filteredFailData;
    $[172] = handleScatterClick;
    $[173] = hoveredDot;
    $[174] = testId;
    $[175] = t50;
  } else {
    t50 = $[175];
  }
  let t51;
  if ($[176] !== chartWidth || $[177] !== t36 || $[178] !== t44 || $[179] !== t45 || $[180] !== t46 || $[181] !== t47 || $[182] !== t48 || $[183] !== t49 || $[184] !== t50) {
    t51 = <ChartContainer config={t33} className="h-[335px] w-full"><ScatterChart width={chartWidth} height={335} margin={t36}>{t40}{t41}{t44}{t45}{t46}{t47}{t48}{t49}{t50}</ScatterChart></ChartContainer>;
    $[176] = chartWidth;
    $[177] = t36;
    $[178] = t44;
    $[179] = t45;
    $[180] = t46;
    $[181] = t47;
    $[182] = t48;
    $[183] = t49;
    $[184] = t50;
    $[185] = t51;
  } else {
    t51 = $[185];
  }
  let t52;
  if ($[186] !== handleMouseDown || $[187] !== t31 || $[188] !== t32 || $[189] !== t51) {
    t52 = <div ref={chartContainerRef} className="w-full h-[363px]" onMouseDown={handleMouseDown} style={t31}>{t32}{t51}</div>;
    $[186] = handleMouseDown;
    $[187] = t31;
    $[188] = t32;
    $[189] = t51;
    $[190] = t52;
  } else {
    t52 = $[190];
  }
  return t52;
});
function _temp5(key) {
  if (STATUS_GROUP.includes(key)) {
    return STATUS_GROUP;
  }
  if (BRANCH_GROUP.includes(key)) {
    return BRANCH_GROUP;
  }
  return [key];
}
function _temp6(run) {
  return run.status === "pass" || run.status === "fail" || run.status === "error";
}
function _temp7(a_1, b_1) {
  return a_1 - b_1;
}
function _temp8(a_2, b_2) {
  return a_2 - b_2;
}